'use client';

import clsx from 'clsx';
import { useEffect, useState } from 'react';

import { useAuth } from '@/entities/user';
import { partitionVisibleNotifications } from '@/features/system-alerts/model/advertisement';
import { SystemAlertModal } from '@/features/system-alerts/ui/system-alert-modal';
import { useSystemAlerts } from '@/features/system-alerts/use-system-alerts';
import { Button, XMarkIcon } from '@/shared';

import { useAlertMarginTop } from '../hooks/use-alert-margin-top';
import { useTopbarHeight } from '../hooks/use-topbar-height';
import { useViewedNotifications } from '../hooks/use-viewed-notifications';
import { getAlertId } from '../utils/get-alert-id';

import { AdvertisementBanner } from './advertisement-banner';

export const AlertWidget = ({
	className,
	withSmartMarginTop,
	offset = 8,
	defaultMarginTop,
}: {
	className?: string;
	withSmartMarginTop?: boolean;
	offset?: number;
	defaultMarginTop: string;
}) => {
	const { alerts: allAlerts, removeAlert } = useSystemAlerts();
	const { user } = useAuth();
	const [isOpen, setIsOpen] = useState(false);
	const [isMounted, setIsMounted] = useState(false);

	const { markAsViewed, viewedNotificationIds } = useViewedNotifications();

	const visible = partitionVisibleNotifications(
		allAlerts,
		user?.contentPermissionLevel,
	);
	const alerts = visible.alerts.filter(
		(alert) => !viewedNotificationIds.has(alert.id),
	);
	const advertisements = user
		? visible.advertisements.filter(
				(notification) => !viewedNotificationIds.has(notification.id),
			)
		: [];
	const shouldRender = alerts.length > 0 || advertisements.length > 0;
	const topbarHeight = useTopbarHeight(withSmartMarginTop ?? false);
	const marginTop = useAlertMarginTop(
		withSmartMarginTop ?? false,
		topbarHeight,
		offset,
		defaultMarginTop,
	);

	const currentAlert = alerts[0];
	const currentAlertId = currentAlert ? getAlertId(currentAlert) : null;

	useEffect(() => {
		if (shouldRender && currentAlert) {
			setIsMounted(true);
		} else {
			setIsMounted(false);
		}
	}, [shouldRender, currentAlert]);

	const handleOpen = () => {
		setIsOpen(true);
	};

	const handleClose = () => {
		setIsOpen(false);
		if (currentAlertId) {
			markAsViewed(currentAlertId);
			// Удаляем уведомление из списка
			removeAlert(currentAlertId);
		}
	};

	if (!shouldRender) {
		return null;
	}

	return (
		<div style={{ marginTop }} className={clsx('space-y-2', className)}>
			{advertisements.map((notification) => (
				<AdvertisementBanner
					key={notification.id}
					notification={notification}
					onDismiss={() => {
						markAsViewed(notification.id);
						removeAlert(notification.id);
					}}
				/>
			))}
			{currentAlert && (
				<div
					className={clsx(
						'relative flex w-full overflow-hidden rounded-lg transition-all duration-[1000ms] ease-out md:duration-[2000ms]',
						{
							'max-h-0 opacity-10': !isMounted,
							'max-h-32 opacity-100': isMounted,
						},
					)}
				>
					<div className="absolute inset-0 z-0 rounded-lg bg-white" />

					<div
						className={clsx(
							'relative flex w-full items-center justify-between gap-2 rounded-lg px-4 py-2',
							{
								'border border-yellow bg-yellow/10':
									currentAlert.level === 'warning',
								'border border-red bg-red-light':
									currentAlert.level === 'error',
								'border border-indigo bg-indigo/10':
									currentAlert.level === 'info',
							},
						)}
					>
						<div className="mx-auto flex min-w-0 flex-1 flex-col items-center justify-center gap-2 pl-1 pr-2 text-gray-2 md:flex-row">
							<span className="line-clamp-3 md:line-clamp-none md:truncate">
								{currentAlert.widgetTitle || currentAlert.description || ''}
							</span>
							<Button variant="outline" size="sm" onClick={handleOpen}>
								Подробнее
							</Button>
						</div>
						<button type="button" onClick={handleClose}>
							<XMarkIcon className="size-4 flex-shrink-0" />
						</button>
					</div>
					<SystemAlertModal
						alerts={alerts}
						isOpen={isOpen}
						onClose={handleClose}
					/>
				</div>
			)}
		</div>
	);
};
